<template lang="pug">
section.calendar-picker
    //- Calendar Header
    header.calendar-header
        .calendar-month-selector
            span.arrow.left(@click="adjustMonth(-1)")
                include ../resources/arrow-button.svg
            span.selected-month {{ Months[timeframe.month] }} {{ timeframe.year }}
            span.arrow.right(@click="adjustMonth()")
                include ../resources/arrow-button.svg
    //- Actual container
    .calendar-dates-container
        //- Day (sunday, monday, ...) list
        .calendar-row.day-header
            span.day.flex-center(v-for="day,index in Days" :key="day" :style="{'--column': index}") {{ day }}
        //- Calendar Rows 
        .calendar-row-container(v-for="week, index in eventWeeks" :key="index")
            //- Background (date number, box)
            .calendar-row.calendar-row-fill
                template(v-for="day in daysOfWeek(timeframe.year, timeframe.month, index)" :key="day")
                    .calendar-day-bg.flex-center(:class="{'out-of-month': day.getMonth() !== timeframe.month}") 
                        .calendar-date.flex-center(
                            :class="{'drop-off': dropOffDate?.valueOf() == day.valueOf(), 'pick-up': pickUpDate?.valueOf() == day.valueOf()}" 
                            @click="selectDate(day)"
                        ) {{ day.getDate() }}
    footer.dates
        .drop-off-date 
            span.description Dropoff Date:
            template(v-if="dropOffDate")
                span.date {{formatDate(dropOffDate)}}
                span at
                select(v-model="dropOffTime")
                    option(:value="null" hidden disabled selected) Select a drop off time
                    option(v-for="availableDropOffTime in availableDropOffTimes" :key="availableDropOffTime.getTime()" :value="availableDropOffTime") {{formatTime(availableDropOffTime)}}
        .pick-up-date(v-if="!singleSelect")
            span.description Pickup Date:
            template(v-if="pickUpDate")
                span.date {{formatDate(pickUpDate)}}
                span at
                select(v-model="pickUpTime")
                    option(:value="null" hidden disabled selected) Select a pick up time
                    option(v-for="availablePickUpTime in availablePickUpTimes" :key= "availablePickUpTime.getTime()" :value="availablePickUpTime") {{formatTime(availablePickUpTime)}}
        
</template>

<script setup lang="ts">
import { daysOfWeek, weeksInMonth } from '@busy-human/k9-library';
import { computed, ref, watch } from 'vue';
import moment from "moment";

type Props = {
	singleSelect?: boolean
}

const props = defineProps<Props>();

type Emits = {
	(e: 'selectEvent', eventId: string): void
	(e: 'update:dropOffTime', dropOffTime: string | null): void
	(e: 'update:pickUpTime', pickUpTime: string | null): void
}

const emit = defineEmits<Emits>();

const Days = [
	'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];
const Months = [
	'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'
];

// -- Constants 
const timeframe = ref<{month: number, year: number}>({
	month: new Date().getMonth(),
	year: new Date().getFullYear()
});

// -- Ref
const dropOffDate = ref<Date | null>(null);
const pickUpDate = ref<Date | null>(null);

const dropOffTime = ref<Date | null>(null);
const pickUpTime = ref<Date | null>(null);

// -- Computed
const eventWeeks = computed(() => {
	const weekCount = weeksInMonth(timeframe.value.year, timeframe.value.month);

	return Array(weekCount).fill(null).map(() => []);;
});

const THIRTY = 30;
const SEVENTEEN = 17;

const availableDropOffTimes = computed(() => {
	if(!dropOffDate.value)
	{return [];}
    
	const dropOffTimes: Date[] = [];

	for(let i = moment(dropOffDate.value).set({hour: 9}); i.hour() < SEVENTEEN; i.add(THIRTY, 'minutes')){
		dropOffTimes.push(i.toDate());
	}

	return dropOffTimes;
});

const availablePickUpTimes = computed(() => {
	if(!pickUpDate.value)
	{return [];}
    
	const pickUpTimes: Date[] = [];

	for(let i = moment(pickUpDate.value).set({hour: 9}); i.hour() < SEVENTEEN; i.add(THIRTY, 'minutes')){
		pickUpTimes.push(i.toDate());
	}
	return pickUpTimes;
});

// -- user intraction
const adjustMonth = (delta = 1) => {
	const tmp = new Date(timeframe.value.year, timeframe.value.month + delta, 1);
	timeframe.value = { year: tmp.getFullYear(), month: tmp.getMonth() };
};

const selectDate = (date: Date) => {
	if(props.singleSelect){
		if(dropOffDate.value && date.valueOf() === dropOffDate.value.valueOf()){
			dropOffDate.value = null;
			dropOffTime.value = null;
		}else{
			dropOffDate.value = date;
			dropOffTime.value = null;
		}
	}else if(pickUpDate.value && date.valueOf() === pickUpDate.value.valueOf()){
		pickUpDate.value = null;
		pickUpTime.value = null;
	}else if(dropOffDate.value && date.valueOf() === dropOffDate.value.valueOf()){
		dropOffDate.value = null;
		dropOffTime.value = null;
	}else if(dropOffDate.value && date.valueOf() > dropOffDate.value.valueOf()){
		pickUpDate.value = date;
		pickUpTime.value = null;
	}else{
		dropOffDate.value = date;
		dropOffTime.value = null;
	}
};

watch(dropOffTime, (val:Date | null) => {
	if(val)
	{emit('update:dropOffTime', val.toISOString());}
});

watch(pickUpTime, (val:Date | null) => {
	if(val)
	{emit('update:pickUpTime', val.toISOString());}
});

// -- Helpers
const formatDate = (date: Date) => moment(date).format('MMM D');

const formatTime = (date: Date) => moment(date).format('hh:mm A');
</script>

<style lang="scss" scoped>
section.calendar-picker {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;

    .calendar-header{
        width: 100%;

        span{
            cursor: pointer;
        }

        .calendar-month-selector{
            $selector-padding: 1rem;

            display: flex;
            align-items: center;
            justify-content: space-between;
            width: calc(100% - 2 * $selector-padding);
            padding: 0.5rem $selector-padding;
            background: #F1F1F1;
        }

        .calendar-month-selector .arrow{
            display: flex;
            align-items: center;
            justify-content: center;
            background: grey;
            border-radius: 100%;
        }

        .calendar-month-selector .left{
            transform: rotate(180deg);
        }

        .selected-month{
            margin: 0 2rem;
            cursor: default;
        }
    }

    .calendar-dates-container {
        display: grid;
        flex-grow: 1;
        grid-template-rows: min-content;
        grid-auto-rows: auto;

        // grid-auto-rows: 1fr; // If we want all the rows to be the same height
        .calendar-row-container {
            display: grid;
            flex-grow: 1;
            grid-template-areas: "fill";

        }
        
        .calendar-row-fill {
            grid-area: fill;
        }

        .calendar-row {
            display: grid;
            grid-template-columns: repeat(7, 1fr);

        }

        .calendar-day-bg {
            padding-left: .5ex;
        }

        .calendar-date {
            width: 1rem;
            height: 1rem;
            padding: 0.5rem;
            cursor: pointer;
            border-radius: 5px;

        }
        
        .calendar-date.pick-up {
            background: #a70024;
        }

        .calendar-date.drop-off,
        .calendar-date.pick-up {
            outline: 1px solid #a70024;
        }

        .calendar-day-bg.out-of-month {
            color: lightgray;
        }
    }

    footer.dates{
        display: flex;
        margin-top: 1rem;

        .drop-off-date,
        .pick-up-date{
            display: flex;
            gap: 0.35rem;
            width: 50%;

            // .date{
            //     font-weight: bold;
            // }
        }
    }
}
</style>